import React, { Suspense, useEffect, useState } from 'react';
import { useMagic } from '../../context/MagicContext';
import { getRealmById, DEFAULT_REALM_ID } from '../../registry';
import { RealmBackground } from '../RealmBackground/RealmBackground';
import { TopHudCounters } from '../TopHudCounters';
import { BottomNav } from '../BottomNav';
import { Hotspot } from '../Hotspot';
import { ProgressionEngine } from '../../engine/ProgressionEngine';
import { ClueScroll } from '../ClueScroll';
import { HatchCinematic } from '../HatchCinematic/HatchCinematic';
import './Cathedral.css';

/**
 * Loading fallback for lazy-loaded realms
 */
function RealmLoader() {
    return (
        <div className="realm-loader">
            <div className="loader-spinner">✨</div>
            <p>Opening realm...</p>
        </div>
    );
}

/**
 * Cathedral - Main shell container
 * Manages layered backgrounds and realm rendering
 */
export function Cathedral() {
    const { activeRealmId, showEggHatch, setShowEggHatch, pendingClueId } = useMagic();
    const realm = getRealmById(activeRealmId) || getRealmById(DEFAULT_REALM_ID);
    const isHomeLocked = activeRealmId === 'home';

    const [notice, setNotice] = useState('');
    const [step, setStep] = useState(ProgressionEngine.getCurrentStep());
    useEffect(() => ProgressionEngine.subscribe(result => {
        setStep(ProgressionEngine.getCurrentStep());
        if (result.itemsUnlocked.length) setNotice('A new treasure is waiting on your shelf!');
        else if (result.starsAwarded) setNotice('You earned ' + result.starsAwarded + (result.starsAwarded === 1 ? ' star.' : ' stars.'));
        else if (result.discoveryCompleted) setNotice('Secret discovered!');
    }), []);
    useEffect(() => { if (notice) { const timer = setTimeout(() => setNotice(''), 5000); return () => clearTimeout(timer); } }, [notice]);
    useEffect(() => {
        const failure = () => setNotice('Your device could not save this change. Please free some storage and try again.');
        window.addEventListener('sparkle-storage-error', failure);
        return () => window.removeEventListener('sparkle-storage-error', failure);
    }, []);

    if (!realm) {
        return <div>Error: No realm found</div>;
    }

    const RealmComponent = realm.component;

    return (
        <div className="cathedral">
            {/* Z-1: Realm Background & Effects */}
            <RealmBackground realmId={activeRealmId} />

            {/* Z-4: Active realm content */}
            <main className={`realm-content ${isHomeLocked ? '' : 'scrollable'}`}>
                <Suspense fallback={<RealmLoader />}>
                    <RealmComponent />
                </Suspense>

                {/* Hotspots for discovery - rendered inside main to maintain relative scaling if applicable */}
                {!isHomeLocked && step?.type === 'hotspot' && step.realm === activeRealmId && <div className="realm-secret">
                    <img src={activeRealmId === 'media' ? '/ui/items/item_06.png' : activeRealmId === 'diary' ? '/ui/items/item_04.png' : '/ui/items/item_15_discovery.png'} alt="A secret is glowing here" />
                    <Hotspot id={step.hotspotId} realm={step.realm} x={0} y={0} width={80} height={90} />
                </div>}
            </main>

            {/* Z-6: HUD counters (top) */}
            <TopHudCounters />

            {/* Z-6: Navigation (bottom) */}
            <BottomNav />
            {notice && <div className="reward-toast" role="status">{notice}</div>}
            {pendingClueId && <ClueScroll key={pendingClueId} clueId={pendingClueId} />}

            {/* Z-1000: Egg Hatch Cinematic Overlay */}
            {showEggHatch && (
                <HatchCinematic onClose={() => setShowEggHatch(false)} />
            )}
        </div>
    );
}

export default Cathedral;
